import { type RefObject, useEffect } from "react";

/**
 * Close a popover/dropdown when the user clicks (or touches) outside `ref`.
 *
 * Listens in the **capture phase** so it fires before a child's
 * `stopPropagation()` (e.g. ReactFlow panes, table cells) can swallow the
 * event — the same reason the inspector kit's `Select` uses capture. No-op
 * while `active` is false, so a closed picker adds zero global listeners.
 */
export function useOutsideClick(
  ref: RefObject<HTMLElement | null>,
  onOutside: () => void,
  active = true,
): void {
  useEffect(() => {
    if (!active) return;
    const handler = (e: MouseEvent | TouchEvent) => {
      const el = ref.current;
      if (el && !el.contains(e.target as Node)) onOutside();
    };
    document.addEventListener("mousedown", handler, true);
    document.addEventListener("touchstart", handler, true);
    return () => {
      document.removeEventListener("mousedown", handler, true);
      document.removeEventListener("touchstart", handler, true);
    };
  }, [ref, onOutside, active]);
}
